import React, { useRef, useEffect } from 'react';
import './Certification.css';
import { Shield, Award, Lightbulb, ArrowRight } from 'lucide-react';
import OHC from './ohc.png';

const Certification = () => {
  const scrollToSection = () => {
    const section = document.getElementById('target-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToContact = () => {
    window.location.href = '/contact';
  };

  const overviewTextRef = useRef(null);
  const benefitRefs = useRef([]);
  const techContentRef = useRef(null);
  const techImageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('in-view');
        });
      },
      { threshold: 0.3 }
    );

    const elements = [
      overviewTextRef.current,
      techContentRef.current,
      techImageRef.current,
      ...benefitRefs.current
    ].filter(Boolean);

    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const benefits = [
    {
      icon: Shield,
      title: 'Regulatory Compliance',
      description: 'Meet industry standards and avoid costly penalties while ensuring adherence to safety regulations.'
    },
    {
      icon: Award,
      title: 'Employee Well-being',
      description: 'Create a healthier, more productive workforce with improved morale and reduced absenteeism.'
    },
    {
      icon: Lightbulb,
      title: 'Incident Reduction',
      description: 'Minimize accidents, cut downtime, and boost efficiency through proven safety practices.'
    }
  ];

  return (
    <div className="certification-container">
      {/* Hero Section */}
      <section className="certification-hero">
        <div className="certification-hero-content">
          <h1 className="certification-hero-title">
            Certification <span className="certification-hero-accent">Program</span>
          </h1>
          <p className="certification-hero-subtitle">
            Achieve Occupational Health certification for your company with Erasys WhiteMatter's all-in-one training solution.
          </p>
          <div className="certification-hero-buttons">
            <button className="certification-btn certification-btn-primary" onClick={scrollToSection}>
              Learn More
              <ArrowRight className="certification-btn-icon" />
            </button>
            <button className="certification-btn certification-btn-secondary" onClick={goToContact}>
              Contact Us
              <ArrowRight className="certification-btn-icon" />
            </button>
          </div>
        </div>
      </section>

      {/* Why Certification Matters Section */}
      <section className="certification-overview" id="target-section">
        <div className="certification-section-wrapper">
          <div className="certification-section-header" ref={overviewTextRef}>
            <h2 className="certification-section-title">Why Certification Matters</h2>
            <p className="certification-section-subtitle">
              Setting the standard for workplace health and safety
            </p>
          </div>
          
          <div className="certification-benefits-grid">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className="certification-benefit-card"
                  ref={(el) => (benefitRefs.current[index] = el)}
                >
                  <div className="certification-benefit-icon">
                    <IconComponent strokeWidth={1.5} />
                  </div>
                  <h3 className="certification-benefit-title">{benefit.title}</h3>
                  <p className="certification-benefit-description">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technology Integration Section */}
      <section className="certification-technology">
        <div className="certification-section-wrapper">
          <div className="certification-technology-grid">
            <div className="certification-technology-content" ref={techContentRef}>
              <h2 className="certification-technology-title">Advanced Technology Integration</h2>
              <p className="certification-technology-description">
                Our certification program leverages cutting-edge technology to streamline compliance, 
                training, and documentation processes.
              </p>
              <ul className="certification-technology-features">
                <li>AI-powered risk assessment</li>
                <li>Real-time compliance monitoring</li>
                <li>Digital certification management</li>
                <li>Integrated training modules</li>
              </ul>
              <a href="/products#whitematter" className="certification-technology-link">
                Learn about our technology
                <ArrowRight className="certification-link-icon" />
              </a>
            </div>
            <div className="certification-technology-image" ref={techImageRef}>
              <img src={OHC} alt="Advanced Technology Integration" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Certification;